import { useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { SellerStaticsDetail } from 'src/layouts/defaultLayoutContainer';
import { loadUsetifulScript, setUsetifulTags } from 'usetiful-sdk';
import { usetifulFragment$key } from './__generated__/usetifulFragment.graphql';
import { useAppState } from './appState';
import { toLocalID } from './helpers';

const Usetiful = ({
    userID,
    eCommerceID,
    eCommerceName,
    sellerStaticsDetail
}: {
    userID: number;
    eCommerceID: number;
    eCommerceName: string;
    sellerStaticsDetail: SellerStaticsDetail;
}) => {
    const { queryData } = useAppState();
    const query = useFragment<usetifulFragment$key>(
        graphql`
            fragment usetifulFragment on Query {
                isSellerMigrating
                treeMenuReferringInfo {
                    isSubscriptionSubStatusBlocked
                    noValidSubscriptionReason
                    subscriptionCancel
                    isTrial
                }
                sellerSetting {
                    userInterfaceSwitchType
                }
                sellerUserInterfaces(first: 50) {
                    nodes {
                        id
                        environment
                        settings
                        isDefault
                        url
                        userInterfaceId
                        sellerUserInterfaceId
                    }
                }
                shopifyAuthEcommerceSettings {
                    environment
                    installed
                    isTestStore
                    shop
                }
                salesChannels(status: ACTIVE) {
                    id
                    detail {
                        id
                        properties {
                            name
                        }
                    }
                    ecommerceSettings {
                        id
                        ecommerceGlobalID
                        ecommerceName
                        remoteUrl
                        isMarketplace
                        definitiveUrl
                    }
                }
            }
        `,
        queryData?.usetifulFragment ?? null
    );

    const additionalInfo = query?.treeMenuReferringInfo;

    const userInterfaceSwitchType = query?.sellerSetting?.userInterfaceSwitchType;
    const defaultSellerUserInterface = query?.sellerUserInterfaces?.nodes?.find((x) => x?.isDefault);

    const shopifyAuthEcommerceSettings = query?.shopifyAuthEcommerceSettings;

    const newUserInterfaceStatus = determineNewUserInterfaceStatus(userInterfaceSwitchType, defaultSellerUserInterface);

    const otherSalesChannel =
        query?.salesChannels?.filter((channel) => toLocalID(channel.id) != 1 && channel.ecommerceSettings?.ecommerceName !== 'NONE') ?? [];

    useEffect(() => {
        if (!window.USETIFUL && userID !== -1) {
            loadUsetifulScript('ea69e155445504c4d08d84c4585e7c9b'); // This contains your unique token key

            console.log('Usetiful script loaded');
        }

        const ensureUsetifulLoaded = () => {
            console.log('Ensuring Usetiful is loaded');
            return new Promise((resolve) => {
                if (window.USETIFUL) {
                    resolve(window.USETIFUL);
                } else {
                    const interval = setInterval(() => {
                        if (window.USETIFUL) {
                            clearInterval(interval);
                            resolve(window.USETIFUL);
                        }
                    }, 1000);
                }
            });
        };

        const runUsetiful = async () => {
            await ensureUsetifulLoaded();
            console.log('Usetiful is loaded');

            if (window.USETIFUL) {
                if (userID !== -1) {
                    const registeredFromDays = sellerStaticsDetail?.registeredOn
                        ? Math.floor((Date.now() - Date.parse(sellerStaticsDetail.registeredOn)) / (1000 * 60 * 60 * 24))
                        : 0;
                    const subscribedFromDays = sellerStaticsDetail?.subscribedOn
                        ? Math.floor((Date.now() - Date.parse(sellerStaticsDetail.subscribedOn)) / (1000 * 60 * 60 * 24))
                        : 0;

                    // Reminder: Usetiful tags must not be boolean values, so we convert them to strings.
                    const usetifulTagsObject = {
                        userId: userID, // This identifies the user and should be unique.
                        Status: sellerStaticsDetail?.state,
                        SubStatus: sellerStaticsDetail?.subState,
                        RegisteredOn: sellerStaticsDetail?.registeredOn ? new Date(Date.parse(sellerStaticsDetail.registeredOn)).toISOString() : '',
                        SubscribedOn: sellerStaticsDetail?.subscribedOn ? new Date(Date.parse(sellerStaticsDetail.subscribedOn)).toISOString() : '',
                        HasNoSubscription: sellerStaticsDetail?.state == 0 && sellerStaticsDetail?.subState == 0 ? 'true' : 'false', //in forma negata per il bug di usetiful -> se non ha subscription -> true
                        registeredFromDays: registeredFromDays,
                        subscribedFromDays: subscribedFromDays,
                        Privacy: sellerStaticsDetail?.privacy ? sellerStaticsDetail?.privacy.toString() : 'false',
                        Orders: sellerStaticsDetail?.orders,
                        Quantity: sellerStaticsDetail?.quantity,
                        Amount: sellerStaticsDetail?.amount,
                        Interactions: sellerStaticsDetail?.interactions,
                        Products: sellerStaticsDetail?.products,
                        Designs: sellerStaticsDetail?.designs,
                        PrintfulProducts: sellerStaticsDetail?.printfulProducts,
                        PrintfulOrders: sellerStaticsDetail?.printfulOrders,
                        LastDayOrders: sellerStaticsDetail?.lastDayOrders,
                        LastOrderDate: sellerStaticsDetail?.lastOrderDate ? new Date(Date.parse(sellerStaticsDetail.lastOrderDate)).toISOString() : '',
                        TotalFees: sellerStaticsDetail?.totalFees,
                        OrdersAsPaying: sellerStaticsDetail?.ordersAsPaying,
                        PreviousDayItemsOrdered: sellerStaticsDetail?.previousDayItemsOrdered,
                        PlanName: sellerStaticsDetail?.planName,
                        ConfiguredOn: sellerStaticsDetail?.configuredOn ? new Date(Date.parse(sellerStaticsDetail.configuredOn)).toISOString() : '',
                        IsExtraViews: sellerStaticsDetail?.isExtraViews ? sellerStaticsDetail?.isExtraViews.toString() : 'false',
                        isSubscriptionSubStateBlocked: additionalInfo?.isSubscriptionSubStatusBlocked
                            ? additionalInfo?.isSubscriptionSubStatusBlocked.toString()
                            : 'false',
                        UserSource: sellerStaticsDetail?.userSource,
                        isInTrial: additionalInfo?.isTrial ? additionalInfo?.isTrial.toString() : 'false',
                        newUserInterfaceStatus: newUserInterfaceStatus,
                        eCommerceID: eCommerceID,
                        eCommerceName: eCommerceName ?? 'none',
                        noValidSubscriptionReason: additionalInfo?.noValidSubscriptionReason,
                        subscriptionCancel: additionalInfo?.subscriptionCancel,
                        ...(shopifyAuthEcommerceSettings && {
                            isShopifyAppInstalledOnMainChannel: (shopifyAuthEcommerceSettings?.installed).toString() || 'false'
                        }),
                        activeSalesChannelsCount: otherSalesChannel.length,
                        isSellerMigrating: query?.isSellerMigrating ? 'true' : 'false'
                    };

                    setUsetifulTags(usetifulTagsObject);

                    console.log('Usetiful tags set, data: ', usetifulTagsObject);
                }
            }
        };

        runUsetiful();
    }, [userID, eCommerceName, additionalInfo]);

    return null;
};

const determineNewUserInterfaceStatus = (userInterfaceSwitchType: string | undefined, defaultSellerUserInterface: any): string => {
    if (userInterfaceSwitchType !== 'FULL_SWITCH') {
        return '';
    } else if (
        defaultSellerUserInterface == null ||
        (defaultSellerUserInterface.userInterfaceId === 1 && (defaultSellerUserInterface.settings == null || defaultSellerUserInterface.settings === '{}'))
    ) {
        return 'Never tested';
    } else if (
        defaultSellerUserInterface.userInterfaceId === 1 &&
        defaultSellerUserInterface.settings !== null &&
        defaultSellerUserInterface.settings !== '{}'
    ) {
        return 'Switched back';
    } else if (defaultSellerUserInterface.userInterfaceId === 2) {
        return 'New UI';
    } else {
        return '';
    }
};

export default Usetiful;
